/**
 * Cola de subida visible dentro de una nota: una fila por archivo con miniatura,
 * barra de progreso real (bytes enviados), tiempo restante, estado y los botones
 * Cancelar / Reintentar / Quitar. Es solo presentación: el estado vive en
 * `colaSubidaStore.ts` y la lógica pura en `utils/colaSubida.ts`.
 *
 * `data-testid`: nm-cola, nm-cola-item (con data-estado y data-nombre),
 * nm-progreso (role=progressbar con aria-valuenow), nm-estado, nm-cancelar,
 * nm-reintentar, nm-quitar, nm-error, nm-aviso-subiendo, nm-aviso-pantalla.
 */

import { Button, Progress } from "antd";
import {
  CloseOutlined,
  FileOutlined,
  ReloadOutlined,
  VideoCameraOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  estaActivo,
  formatBytes,
  formatEta,
  formatVelocidad,
  textoEstado,
  type EstadoItemCola,
} from "../../../utils/colaSubida";
import type { ItemNota } from "./colaSubidaStore";
import "../../../Css/notas-movil.css";

const ESTADO_PROGRESO: Partial<Record<EstadoItemCola, "normal" | "active" | "exception" | "success">> = {
  comprimiendo: "active",
  subiendo: "active",
  reintentando: "exception",
  sin_conexion: "exception",
  procesando: "active",
  listo: "success",
  error: "exception",
};

/** Línea de detalle: "12,3 MB / 250 MB · 4% · 3 min 10 s restantes · 1,2 MB/s". */
export function detalleProgreso(i: Pick<ItemNota, "estado" | "bytes" | "total" | "porcentaje" | "etaSeg" | "velocidadBps">): string {
  switch (i.estado) {
    case "en_espera":
      return `${formatBytes(i.total)} · esperando su turno`;
    case "comprimiendo":
      return `${formatBytes(i.total)} · reduciendo el peso de la foto`;
    case "procesando":
      return `${formatBytes(i.total)} · el servidor está preparando el archivo`;
    case "listo":
      return `${formatBytes(i.total)} · subido`;
    case "error":
    case "cancelado":
      return formatBytes(i.total);
    default: {
      const partes = [`${formatBytes(i.bytes)} / ${formatBytes(i.total)}`, `${i.porcentaje}%`];
      const eta = formatEta(i.etaSeg);
      if (eta && i.estado === "subiendo") partes.push(`${eta} restantes`);
      const vel = formatVelocidad(i.velocidadBps);
      if (vel && i.estado === "subiendo") partes.push(vel);
      return partes.join(" · ");
    }
  }
}

export function NotaColaSubida({
  items,
  avisoPantalla,
  onCancelar,
  onReintentar,
  onQuitar,
  onDescartarAviso,
}: {
  items: ItemNota[];
  avisoPantalla: boolean;
  onCancelar: (id: string) => void;
  onReintentar: (id: string) => void;
  onQuitar: (id: string) => void;
  onDescartarAviso: () => void;
}) {
  if (items.length === 0) return null;
  const hayActivos = items.some((i) => estaActivo(i.estado));

  return (
    <div className="nm-cola" data-testid="nm-cola">
      {hayActivos && (
        <div className="nm-aviso nm-aviso-subiendo" role="status" data-testid="nm-aviso-subiendo">
          <strong>No cierres ni bloquees el celular mientras sube.</strong> Puedes seguir escribiendo
          o agregando más archivos.
        </div>
      )}
      {avisoPantalla && hayActivos && (
        <div className="nm-aviso nm-aviso-pantalla" role="alert" data-testid="nm-aviso-pantalla">
          <WarningOutlined /> Pantalla bloqueada o app en segundo plano: mantén la app abierta. Si la
          subida se cortó, retoma sola desde donde quedó.{" "}
          <Button type="link" size="small" onClick={onDescartarAviso} style={{ padding: 0 }}>
            Entendido
          </Button>
        </div>
      )}

      {items.map((i) => {
        const activo = estaActivo(i.estado);
        const mostrarBarra = i.estado !== "cancelado";
        return (
          <div
            key={i.id}
            className="nm-cola-item"
            data-testid="nm-cola-item"
            data-estado={i.estado}
            data-nombre={i.nombre}
          >
            <div className="nm-cola-thumb" aria-hidden>
              {i.previewUrl ? (
                <img src={i.previewUrl} alt="" />
              ) : i.kind === "video" ? (
                <VideoCameraOutlined />
              ) : (
                <FileOutlined />
              )}
            </div>
            <div className="nm-cola-cuerpo">
              <div className="nm-cola-nombre" title={i.nombre}>
                {i.nombre}
              </div>
              <div className="nm-cola-detalle" data-testid="nm-estado">
                <strong>{textoEstado(i)}</strong>
                {i.mensaje && i.estado !== "error" ? ` — ${i.mensaje}` : ""}
              </div>
              {mostrarBarra && (
                <div
                  data-testid="nm-progreso"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={i.estado === "procesando" ? 100 : i.porcentaje}
                >
                  <Progress
                    percent={i.estado === "procesando" ? 100 : i.porcentaje}
                    size="small"
                    showInfo={false}
                    status={ESTADO_PROGRESO[i.estado] ?? "normal"}
                  />
                </div>
              )}
              <div className="nm-cola-detalle">{detalleProgreso(i)}</div>
              {i.estado === "error" && i.mensaje && (
                <div className="nm-cola-error" data-testid="nm-error" role="alert">
                  {i.mensaje}
                </div>
              )}
              <div className="nm-cola-acciones">
                {activo && i.estado !== "procesando" && (
                  <Button
                    size="middle"
                    icon={<CloseOutlined />}
                    onClick={() => onCancelar(i.id)}
                    data-testid="nm-cancelar"
                  >
                    Cancelar
                  </Button>
                )}
                {(i.estado === "error" || i.estado === "cancelado") && (
                  <>
                    {/* Un archivo que el servidor rechazó (tipo, imagen falsa, peso) no mejora
                        reintentando: solo se ofrece quitarlo. */}
                    {!(i.estado === "error" && i.noReintentable) && (
                      <Button
                        type="primary"
                        size="middle"
                        icon={<ReloadOutlined />}
                        onClick={() => onReintentar(i.id)}
                        data-testid="nm-reintentar"
                      >
                        Reintentar
                      </Button>
                    )}
                    <Button size="middle" onClick={() => onQuitar(i.id)} data-testid="nm-quitar">
                      {i.estado === "error" && i.noReintentable ? "Entendido" : "Quitar"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
